import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Button,
  Box,
  Chip,
  IconButton,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import type { SongData } from "../types";
import { getAlbumArt } from "../utils/albumArt";

interface MatchupCardProps {
  song: SongData;
  seedNumber: number;
  onPick: () => void;
  color: string;
}

export default function MatchupCard({
  song,
  seedNumber,
  onPick,
  color,
}: MatchupCardProps) {
  const videoId = song.AlternativeVersion || song.URL;
  const albumArt = getAlbumArt(song.AlbumId) || song.AlbumArt;

  return (
    <Card
      id={`matchup-card-${seedNumber}`}
      sx={{
        flex: 1,
        maxWidth: 500,
        border: `2px solid ${color}`,
        transition: "transform 0.2s",
        "&:hover": { transform: "scale(1.02)" },
      }}
    >
      {albumArt && (
        <CardMedia
          id={`matchup-art-${seedNumber}`}
          component="img"
          height="200"
          image={albumArt}
          alt={song.AlbumName}
          sx={{ objectFit: "cover" }}
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            if (song.AlbumArt && target.src !== song.AlbumArt) {
              target.src = song.AlbumArt;
            }
          }}
        />
      )}
      <CardContent>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
          <Chip label={`Seed ${seedNumber}`} size="small" sx={{ bgcolor: color, color: "#fff" }} />
          <Chip label={song.Genre} size="small" variant="outlined" />
        </Box>
        <Typography id={`matchup-song-${seedNumber}`} variant="h6" gutterBottom>
          {song.SongName}
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {song.Artist} — {song.AlbumName} ({song.YearReleased})
        </Typography>
        {song.Features !== "None" && (
          <Typography variant="caption" color="text.secondary">
            ft. {song.Features}
          </Typography>
        )}

        <Box sx={{ mt: 2 }}>
          <Box
            sx={{ borderRadius: 2, overflow: "hidden", mb: 2, width: "100%" }}
          >
            <iframe
              width="100%"
              height="200"
              src={`https://www.youtube.com/embed/${videoId}`}
              title={song.SongName}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <Button
            id={`matchup-pick-${seedNumber}`}
            variant="contained"
            fullWidth
            onClick={onPick}
            sx={{ bgcolor: color, "&:hover": { bgcolor: color, opacity: 0.9 } }}
          >
            Pick Winner
          </Button>
          {song.Genius && (
            <IconButton
              size="small"
              href={song.Genius}
              target="_blank"
              rel="noopener"
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}
